
import { CellStyle } from "xlsx-js-style";
import { Row, Rows } from "../../../interfaces";
import { formats, getCell, withCellStyles } from "../../../utils";

import { BmTemplateInternals, BudgetModalTemplateCurrentState } from "../budget-helper-internal";
import { columnIndexToExcelLetter, generateFormulaForVat, generateSumFormulaAverageForYear, generateSumFormulaForRow, generateSumFormulaForRowForYears } from "../../../utils/functions";
import { backgroundGray, leftIntendBold, leftIntendTwo, textBold } from "../../../excelStyles";
import { BudgetModelFormGroup } from "../../../../models/budget-model/pojo/bm-form";
import { BmItemValue, BmMgmFeeGroupValue } from "../../../../models/budget-model/pojo/bm-classes-values";
import { BmPeriodEnum } from "../../../../models/budget-model/pojo/bm-classes";

export class ManagementFeesSection {
  private readonly NUMBER_OF_QUARTERS = 4;
  constructor(
    private readonly budgetForm: BudgetModelFormGroup,
    private readonly helper: BmTemplateInternals,
  ) { }

  fillManagementFeesRows(rows: Rows, current: BudgetModalTemplateCurrentState): void {
    this.prepareManagementFeesMainRow(rows, current);
    this.prepareManagementFeesInternal(rows, current);
  }

  private prepareManagementFeesMainRow(newData: Rows, current: BudgetModalTemplateCurrentState): void {
    const rowManagementFees: Row = this.prepareManagementFeesTotalRow(this.budgetForm, current.rowIndex);
    this.helper.pushYearsTotalCellBold(rowManagementFees, current.rowIndex, current.yearsIndexArray);
    this.helper.prepareCellForTotalPcnt(rowManagementFees, current, true);
    this.helper.setCellForTotalPcntOfCommitment(rowManagementFees, current.rowIndex, current.indexOfTotalColumn, true);
    newData.push(rowManagementFees);
    current.indexArrayForTotalExpenses.push(current.rowIndex);
    current.rowIndex++;
  }

  private prepareManagementFeesInternal(newData: Rows, current: BudgetModalTemplateCurrentState): void {
    this.budgetForm.value.modelData.mgmFee.groups.forEach((group) => {
      const subGroupRow: Row = [];
      subGroupRow.push(withCellStyles(group.name, leftIntendBold as CellStyle));
      const groupUpdated = this.helper.setNullToDataAfterYearForBmItems(group.total as BmItemValue[], false);
      groupUpdated.forEach((total, totalIndex) =>
        this.helper.pushSumOrValueWithZeroIndex(subGroupRow, totalIndex, total, current.rowIndex)
      );
      this.helper.pushYearsTotalCellBold(subGroupRow, current.rowIndex, current.yearsIndexArray);
      this.helper.prepareCellForTotalPcnt(subGroupRow, current, true);
      this.helper.setCellForTotalPcntOfCommitment(subGroupRow, current.rowIndex, current.indexOfTotalColumn, true);
      newData.push(subGroupRow);
      current.rowIndex++;

      // commitment row
      const commitmentRow = this.prepareCommitmentRow(group as BmMgmFeeGroupValue);
      newData.push(commitmentRow);
      current.rowIndex++;

      // mgm fees %
      const mgmFeesPcntRow = this.prepareManagementFeesPcntRow(group as BmMgmFeeGroupValue, current.rowIndex);
      newData.push(mgmFeesPcntRow);
      current.rowIndex++;

      // discount %
      if (group.subGroups.pcntOfMgmFees.isSelected) {
        const discountRow = this.prepareDiscountPcntRow(group as BmMgmFeeGroupValue, current.rowIndex);
        newData.push(discountRow);
        current.rowIndex++;
      }

      // mgm fees absolute
      const mgFeesRow: Row = this.prepareManagementFeesRow(group as BmMgmFeeGroupValue, current.rowIndex, current.yearsIndexArray);
      this.helper.prepareCellForTotalPcnt(mgFeesRow, current);
      this.helper.setCellForTotalPcntOfCommitment(mgFeesRow, current.rowIndex, current.indexOfTotalColumn, true);

      newData.push(mgFeesRow);
      current.rowIndex++;

      // catch-up
      current.hasCatchUp = group.subGroups.catchUp.isSelected && group.subGroups.commitment.isCatchUpAvailable;
      if (current.hasCatchUp) {
        const catchUpRow: Row = this.prepareCatchUpRow(group as BmMgmFeeGroupValue, current.rowIndex, current.yearsIndexArray);
        this.helper.prepareCellForTotalPcnt(catchUpRow, current);
        this.helper.setCellForTotalPcntOfCommitment(catchUpRow, current.rowIndex, current.indexOfTotalColumn, true);
        newData.push(catchUpRow);
        current.rowIndex++;
      }

      // VAT
      if (group.subGroups.vatPcnt.isSelected) {
        const vatRowPcnt: Row = this.prepareVatPcntRow(group as BmMgmFeeGroupValue, current.rowIndex, current.hasCatchUp);
        this.helper.setEmptyTotalsRowWithStyle(vatRowPcnt);
        newData.push(vatRowPcnt);
        current.rowIndex++;
        const vatRow = this.prepareVatRow(group as BmMgmFeeGroupValue, current.rowIndex, current.yearsIndexArray);
        this.helper.prepareCellForTotalPcnt(vatRow, current);
        this.helper.setCellForTotalPcntOfCommitment(vatRow, current.rowIndex, current.indexOfTotalColumn, true);
        newData.push(vatRow);
        current.rowIndex++;
      }
    });
  }

  /** Builds the "Management fees" values row. */
  private prepareManagementFeesRow(group: BmMgmFeeGroupValue, currentRowIndex: number, yearsInIndexArray: number[]): Row {
    const row: Row = [];
    row.push(withCellStyles("Management fees", leftIntendTwo as CellStyle));
    const items = group.subGroups.mgmFeeValues.items ?? [];
    const itemsUpdated = this.helper.setNullToDataAfterYearForBmItems(items, true);
    itemsUpdated.forEach((mgF, index) => {
      if (index === 0) {
        return;
      }
      this.helper.pushSumOrValueWIthOutZeroIndex(row, index, mgF, currentRowIndex);
    });

    row.push(withCellStyles(getCell({
      f: `${generateSumFormulaForRowForYears(yearsInIndexArray, currentRowIndex)}`,
      z: formats.thousandsSeparator, t: "n",
    }), backgroundGray));
    return row;
  }

  /** Builds the "Commitment" row. */
  private prepareCommitmentRow(group: BmMgmFeeGroupValue): Row {
    const standardValue = group.subGroups.commitment.standardValue;
    const row: Row = [];
    row.push(withCellStyles("Commitment", leftIntendTwo as CellStyle));
    group.subGroups.commitment.items.forEach((cm) => {
      if (cm.period.type === BmPeriodEnum.YEAR) {
        row.push(withCellStyles(getCell({
          v: cm.itemValue ?? standardValue,
          z: formats.thousandsSeparator,
          t: "n",
        }), backgroundGray));
        row.push(null);
      } else {
        row.push(withCellStyles(getCell({ v: cm.itemValue ?? standardValue, z: formats.thousandsSeparator, t: "n" }), null));
      }
    });
    this.helper.setEmptyTotalsRowWithStyle(row);
    return row;
  }

  /** Builds the "Management Fees %" row. */
  private prepareManagementFeesPcntRow(group: BmMgmFeeGroupValue, currentRowIndex: number): Row {
    const row: Row = [];
    row.push(withCellStyles("Management Fees %", leftIntendTwo as CellStyle));
    const standardValue = group.subGroups.mgmFeesPcnt.standardValue;
    const items = this.helper.setNullToDataAfterYearForBmItems(group.subGroups.mgmFeesPcnt.items);

    items.forEach((pcnt, index) => {
      if (pcnt && pcnt.period.type !== BmPeriodEnum.YEAR) {
        row.push(withCellStyles(getCell({
          // standardValue is annual value,
          //  To get quarterly value need divide annual value by 4 cause of value format(%) need to divide by 400
          v: pcnt.itemValue ? (pcnt.itemValue / 100) : standardValue / (this.NUMBER_OF_QUARTERS * 100),
          z: formats.percents, t: "n",
        }), null));
      } else if (pcnt) {
        const start = columnIndexToExcelLetter(index - this.NUMBER_OF_QUARTERS);
        const end = columnIndexToExcelLetter(index - 1);
        row.push(withCellStyles(getCell({
          f: generateSumFormulaForRow(start, end, currentRowIndex),
          z: formats.percentsNoSpace, t: "n",
        }), backgroundGray));
        row.push(null);
      }
    });

    this.helper.setEmptyTotalsRowWithStyle(row);
    return row;
  }

  /** Builds the "Discount %" row. */
  private prepareDiscountPcntRow(group: BmMgmFeeGroupValue, currentRowIndex: number): Row {
    const row: Row = [];
    row.push(withCellStyles("Discount %", leftIntendTwo as CellStyle));
    const items = this.helper.setNullToDataAfterYearForBmItems(group.subGroups.pcntOfMgmFees.items);

    items.forEach((dis, index) => {
      if (dis && dis.period.type !== BmPeriodEnum.YEAR) {
        row.push(withCellStyles(getCell({
          v: (dis.itemValue ?? group.subGroups.pcntOfMgmFees.standardValue) / 100,
          z: formats.percents, t: "n",
        }), null));
      } else if (dis) {
        const start = columnIndexToExcelLetter(index - this.NUMBER_OF_QUARTERS);
        const end = columnIndexToExcelLetter(index - 1);
        row.push(withCellStyles(getCell({
          f: generateSumFormulaAverageForYear(start, end, currentRowIndex),
          z: formats.percentsNoSpace, t: "n",
        }), backgroundGray));
        row.push(null);
      }
    });

    this.helper.setEmptyTotalsRowWithStyle(row);
    return row;
  }

  /** Builds the "Catch Up" row. */
  private prepareCatchUpRow(group: BmMgmFeeGroupValue, currentRowIndex: number, yearsInIndexArray: number[]): Row {
    const row: Row = [];
    row.push(withCellStyles("Catch Up", leftIntendTwo as CellStyle));

    const catchUpUpdated = this.helper.setNullToDataAfterYearForBmItems(group.subGroups.catchUp.items, false);
    catchUpUpdated.forEach((catchUp, catchUpIndex) => {
      this.helper.pushSumOrValueWithZeroIndex(row, catchUpIndex, catchUp, currentRowIndex);
    });

    row.push(withCellStyles(getCell({
      f: `${generateSumFormulaForRowForYears(yearsInIndexArray, currentRowIndex)}`,
      z: formats.thousandsSeparator, t: "n",
    }), backgroundGray));
    return row;
  }

  private prepareVatPcntRow(group: BmMgmFeeGroupValue, currentRowIndex: number, hasCatchUp: boolean): Row {
    const row: Row = [];
    row.push(withCellStyles("VAT %", leftIntendTwo as CellStyle));
    const std = group.subGroups.vatPcnt.standardValue;
    const updated = this.helper.setNullToDataAfterYearForBmItems(group.subGroups.vatPcnt.items);

    updated.forEach((vat, index) => {
      if (vat && vat.period.type !== BmPeriodEnum.YEAR) {
        row.push(withCellStyles(getCell({
          v: (vat.itemValue ?? std) / 100,
          z: formats.percents,
          t: "n",
        }), null));
      } else if (vat) {
        row.push(withCellStyles(getCell({
          f: generateFormulaForVat(currentRowIndex, index, hasCatchUp),
          z: formats.percentsNoSpace, t: "n",
        }), backgroundGray));
        row.push(null);
      }
    });
    return row;
  }

  /** Builds the "VAT" values row. */
  private prepareVatRow(group: BmMgmFeeGroupValue, currentRowIndex: number, yearsInIndexArray: number[]): Row {
    const row: Row = [];
    row.push(withCellStyles("VAT", leftIntendTwo as CellStyle));
    const itemsUpdated = this.helper.setNullToDataAfterYearForBmItems(group.subGroups.vatValues.items);
    itemsUpdated.forEach((vat, index) => {
      if (index === 0) {
        return;
      }
      this.helper.pushSumOrValueWIthOutZeroIndex(row, index, vat, currentRowIndex);
    });
    row.push(withCellStyles(getCell({
      f: `${generateSumFormulaForRowForYears(yearsInIndexArray, currentRowIndex)}`,
      z: formats.thousandsSeparator, t: "n",
    }), backgroundGray));
    return row;
  }

  /** Builds the "Management Fees" total header row. */
  private prepareManagementFeesTotalRow(budgetForm: BudgetModelFormGroup, currentRowIndex: number): Row {
    const row: Row = [];
    row.push(withCellStyles("Management Fees", textBold));
    const initial = budgetForm.controls.modelData.controls.mgmFee.value.total as BmItemValue[];
    const updated = this.helper.setNullToDataAfterYearForBmItems(initial);
    updated.forEach((mgmFee, index) => {
      // skip budget
      if (index === 0) {
        return;
      }
      if (mgmFee === null) {
        row.push(null);
        return;
      }
      this.helper.pushSumOrValueWIthOutZeroIndex(row, index, mgmFee, currentRowIndex, true);
    });
    return row;
  }
}

export interface StartAndEndLetter {
  start: string;
  end: string;
}
